// slider produktów

import React, { FC, useEffect, useRef, useContext, useState } from 'react';
import {
  ButtonBack,
  ButtonNext,
  CarouselProvider,
  CarouselContext,
  DotGroup,
  Slide,
  Slider
} from 'pure-react-carousel';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';
import classnames from 'classnames';

import { useRWD } from 'hooks';
import { IProductListItem } from 'api/types';
import { ProductItem } from 'components/containers';

import styles from 'theme/components/containers/ProductSlider/ProductSlider.module.scss';

// Komponent wewnętrzny do śledzenia pozycji slidera
const SliderContent: FC<{
  products: IProductListItem[];
  itemsPerSlide: number;
  onLoadMore?: () => void;
  hasMore: boolean;
}> = ({ products, itemsPerSlide, onLoadMore, hasMore }) => {
  const carouselContext = useContext(CarouselContext);
  const lastTriggeredSlideRef = useRef<number>(-1);
  const maxTriggeredSlideRef = useRef<number>(-1);
  const previousProductsLengthRef = useRef<number>(0);

  useEffect(() => {
    if (!onLoadMore || !hasMore || !carouselContext) return;

    // Reset refs gdy produkty się zmienią (np. przy zmianie zakładki)
    if (products.length !== previousProductsLengthRef.current) {
      // Jeśli produkty się zmniejszyły (zmiana zakładki), zresetuj refs
      if (products.length < previousProductsLengthRef.current) {
        lastTriggeredSlideRef.current = -1;
        maxTriggeredSlideRef.current = -1;
      }
      previousProductsLengthRef.current = products.length;
    }

    const handleSlideChange = () => {
      const currentSlide = carouselContext.state.currentSlide;
      const totalSlides = products.length;
      // Triggerujemy wcześniej - 2-3 slajdy przed końcem, żeby produkty zdążyły się załadować przed dojechaniem do końca
      const threshold = Math.max(0, totalSlides - itemsPerSlide - 2);
      const isNearEnd = currentSlide >= threshold;
 
      if (
        isNearEnd && 
        currentSlide !== lastTriggeredSlideRef.current &&
        currentSlide > maxTriggeredSlideRef.current &&
        hasMore
      ) {
        lastTriggeredSlideRef.current = currentSlide;
        maxTriggeredSlideRef.current = currentSlide;
        onLoadMore();
      }
    };

    // Subskrybuj zmiany w sliderze
    carouselContext.subscribe(handleSlideChange);
    
    // Sprawdź również aktualną pozycję od razu
    handleSlideChange();

    return () => {
      carouselContext.unsubscribe(handleSlideChange);
    };
  }, [carouselContext, products.length, itemsPerSlide, onLoadMore, hasMore]);

  return (
    <Slider>
      {products.map((product, index) => (
        <Slide
          key={product.id}
          index={index}
          className={styles.slide}>
          <div className={styles.productWrapper}>
            <ProductItem key={product.id} product={product} slide />
          </div>
        </Slide>
      ))}
    </Slider>
  );
};

// typ danych wejściowych
interface IProps {
  products: IProductListItem[];
  itemsPerSlide: number;
  isArrowHidden?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isUpdating?: boolean
}

const ProductSlider: FC<IProps> = ({ products, itemsPerSlide, isArrowHidden, onLoadMore, hasMore = false, isUpdating }) => {
  const { isMobile } = useRWD();
  const [disableAnimation, setDisableAnimation] = useState(false);

  const hideArrows = isArrowHidden || isMobile;
  
  useEffect(() => {
    if (isUpdating) {
      setDisableAnimation(true);
    } else {
      const timeoutId = setTimeout(() => {
        setDisableAnimation(false);
      }, 50);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isUpdating]);
  
  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Components-Containers-ProductSlider'
      )}>
      <CarouselProvider
        naturalSlideWidth={0}
        naturalSlideHeight={0}
        totalSlides={products.length}
        visibleSlides={itemsPerSlide}
        disableAnimation={disableAnimation}
        disableKeyboard>
        <ButtonBack disabled={hideArrows} className={classnames(styles.navButton, styles.back)}>
          <ChevronLeft />
        </ButtonBack>
        <ButtonNext disabled={hideArrows || isUpdating} className={classnames(styles.navButton, styles.next)}>
          <ChevronRight />
        </ButtonNext>
        <SliderContent
          products={products}
          itemsPerSlide={itemsPerSlide}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
        />
        {isMobile && <DotGroup className={styles.trayBar} />}
      </CarouselProvider>
    </div>
  );
};

export default ProductSlider;
